// Activar cobro automatico mensual via Mercado Pago.
// Usa el flujo de preapproval con redireccion al checkout MP.

import { useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import Layout from '../../components/Layout'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'

export default function CargarTarjeta() {
  const { profile, user } = useAuth()
  const navigate = useNavigate()
  const { clienteId } = useParams()

  const [cliente, setCliente] = useState(null)
  const [kit, setKit] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let cancel = false
    async function load() {
      if (!clienteId) { setError('Falta cliente'); setLoading(false); return }
      try {
        const { data, error: clErr } = await supabase
          .from('clientes')
          .select('id, alias, kit_actual, mp_preapproval_id, mp_card_last4, mp_card_brand, kit:kits!clientes_kit_actual_fkey(nombre, precio_susc)')
          .eq('id', clienteId)
          .single()
        if (clErr) throw clErr
        if (cancel) return
        setCliente(data)
        setKit(data?.kit)
      } catch (e) {
        if (!cancel) setError(e.message)
      } finally {
        if (!cancel) setLoading(false)
      }
    }
    load()
    return () => { cancel = true }
  }, [clienteId])

  async function activar() {
    setError(null)
    // Validar que el perfil este completo antes de redirigir a MP
    const nombre = (profile?.nombre || '').trim()
    const phone = (profile?.phone || '').replace(/\D/g, '')
    if (nombre.length < 2 || phone.length < 8) {
      setError('Completá tu nombre y teléfono en "Mi perfil" antes de continuar.')
      return
    }
    setSubmitting(true)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('mp-create-subscription', {
        body: {
          cliente_id: clienteId,
          email: user?.email || profile?.email,
          monto: Number(kit?.precio_susc || 0),
          kit_codigo: cliente?.kit_actual,
        },
      })
      if (invokeErr) throw invokeErr
      if (data?.error) throw new Error(data.error + (data.detalle ? ': ' + JSON.stringify(data.detalle) : ''))

      // Redirigir a MP para que cliente autorice el cobro recurrente
      const url = data.init_point || data.sandbox_init_point
      if (!url) throw new Error('MP no devolvió URL de pago')
      window.location.href = url
    } catch (e) {
      setError(e.message || String(e))
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Layout>
        <main className="ct-main">
          <div className="ct-card">
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Cargando…</div>
          </div>
        </main>
        <CtStyles />
      </Layout>
    )
  }

  return (
    <Layout>
      <main className="ct-main">
        <div className="ct-card">
          <div className="ct-header">
            <div className="ct-header-text">
              <h2>💳 Activar cobro automático</h2>
              <div className="ct-sub">
                Kit <strong>{kit?.nombre}</strong> · ${Number(kit?.precio_susc || 0).toLocaleString('es-UY')}/mes
              </div>
            </div>
          </div>

          <div className="ct-secure-banner">
            <span className="ct-secure-icon">🔒</span>
            <div>
              <strong>Procesado 100% por Mercado Pago</strong>
              <div className="ct-secure-sub">Te llevamos a la pasarela segura de MP. Elegís cómo pagar y autorizás el cobro mensual automático.</div>
            </div>
          </div>

          {cliente?.mp_card_last4 && (
            <div className="ct-warning">
              ⚠️ Ya tenés tarjeta cargada (<strong>{cliente.mp_card_brand?.toUpperCase()} ···· {cliente.mp_card_last4}</strong>). Si activás de nuevo, reemplaza la anterior.
            </div>
          )}

          {/* Datos del cliente que MP usa */}
          {(() => {
            const nombre = (profile?.nombre || '').trim()
            const email = user?.email || profile?.email || ''
            const phone = (profile?.phone || '').trim()
            const nombreOk = nombre.length > 1
            const emailOk = !!email
            const phoneOk = phone.replace(/\D/g, '').length >= 8
            const todoOk = nombreOk && emailOk && phoneOk
            return (
              <div style={{
                marginTop: 16, padding: 14,
                background: todoOk ? '#ecfdf5' : '#fef3c7',
                border: `1px solid ${todoOk ? '#a7f3d0' : '#fcd34d'}`,
                borderRadius: 10,
              }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: todoOk ? '#065f46' : '#78350f', marginBottom: 8 }}>
                  {todoOk ? '✅ Tus datos están completos' : '⚠️ Te faltan datos'}
                </div>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#374151', lineHeight: 1.7 }}>
                  <li>{nombreOk ? '✓' : '✗'} Nombre: <strong>{nombre || '— vacío —'}</strong></li>
                  <li>{emailOk ? '✓' : '✗'} Email: <strong>{email || '— vacío —'}</strong></li>
                  <li>{phoneOk ? '✓' : '✗'} Teléfono: <strong>{phone || '— vacío —'}</strong></li>
                </ul>
                {!todoOk && (
                  <div style={{ marginTop: 10 }}>
                    <Link to="/cliente/perfil"
                      style={{
                        display: 'inline-block',
                        padding: '8px 16px',
                        background: '#1a3461', color: '#fff',
                        borderRadius: 8, fontSize: 13, fontWeight: 700,
                        textDecoration: 'none',
                      }}>
                      Completar mi perfil →
                    </Link>
                  </div>
                )}
              </div>
            )
          })()}

          <div style={{ background: '#f9fafb', borderRadius: 12, padding: 20, margin: '16px 0', border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1a3461', marginBottom: 12 }}>¿Qué pasa cuando activás?</div>
            <ul style={{ margin: 0, paddingLeft: 20, color: '#374151', fontSize: 13, lineHeight: 1.7 }}>
              <li>Te llevamos a Mercado Pago</li>
              <li>Elegís tu método de pago (tarjeta, MP wallet)</li>
              <li>Autorizás el cobro mensual de <strong>${Number(kit?.precio_susc || 0).toLocaleString('es-UY')}</strong></li>
              <li>Volvés a Limpack y ya queda activo</li>
              <li>Cancelás cuando quieras desde tu cuenta</li>
            </ul>
          </div>

          {error && <div className="ct-err" style={{ marginBottom: 12 }}>{error}</div>}

          {(() => {
            const nombre = (profile?.nombre || '').trim()
            const phone = (profile?.phone || '').replace(/\D/g, '')
            const perfilOk = nombre.length >= 2 && phone.length >= 8
            return (
              <div className="ct-actions" style={{ marginTop: 16 }}>
                <button type="button" className="ct-btn-secondary" onClick={() => navigate('/cliente')} disabled={submitting}>
                  ← Cancelar
                </button>
                <button type="button" className="ct-btn-primary" onClick={activar}
                  disabled={submitting || !perfilOk}
                  style={{ flex: 1, opacity: !perfilOk ? 0.5 : 1, cursor: !perfilOk ? 'not-allowed' : 'pointer' }}>
                  {submitting ? 'Redirigiendo a MP…' : '🔒 Activar con Mercado Pago →'}
                </button>
              </div>
            )
          })()}
        </div>
      </main>
      <CtStyles />
    </Layout>
  )
}

function CtStyles() {
  return (
    <style>{`
      .ct-main {
        max-width: 560px; width: 100%; margin: 0 auto;
        padding: 28px 16px 60px; flex: 1; box-sizing: border-box;
      }
      .ct-card {
        background: #fff; border-radius: 20px;
        box-shadow: 0 8px 40px rgba(26,52,97,.08);
        border: 1px solid rgba(26,52,97,.05);
        padding: 24px;
      }
      @media (min-width: 640px) { .ct-card { padding: 32px } }

      .ct-header {
        padding-bottom: 16px; border-bottom: 1px solid #eef0f4; margin-bottom: 18px;
      }
      .ct-header-text h2 { margin: 0 0 6px; color: #1a3461; font-size: 22px; }
      .ct-sub { color: #6b7280; font-size: 13px; line-height: 1.4; }

      .ct-secure-banner {
        display: flex; align-items: center; gap: 12px;
        padding: 12px 14px; background: #ecfdf5; border: 1px solid #a7f3d0;
        border-radius: 10px; color: #065f46; font-size: 13px; line-height: 1.4;
        margin-bottom: 18px;
      }
      .ct-secure-icon { font-size: 22px; }
      .ct-secure-sub { color: #047857; font-size: 12px; margin-top: 2px; }

      .ct-warning {
        padding: 10px 12px; background: #fef3c7; border: 1px solid #fcd34d;
        border-radius: 8px; color: #78350f; font-size: 13px; margin-bottom: 16px;
      }

      .ct-err {
        padding: 10px 12px; background: #fee2e2; border: 1px solid #fca5a5;
        border-radius: 8px; color: #991b1b; font-size: 13px;
      }

      .ct-actions { display: flex; gap: 8px; }
      .ct-btn-primary {
        padding: 13px 18px; border: 0;
        background: #1a3461; color: #fff;
        border-radius: 10px; font-weight: 700; font-size: 15px;
        cursor: pointer; font-family: inherit;
      }
      .ct-btn-primary:disabled { opacity: .6; cursor: not-allowed; }
      .ct-btn-secondary {
        padding: 13px 18px; border: 1px solid #d1d5db;
        background: #fff; color: #374151;
        border-radius: 10px; font-weight: 600; font-size: 14px;
        cursor: pointer; font-family: inherit;
      }
    `}</style>
  )
}
